import { useStoreUserData } from './../../hooks/useStoreUserData';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '../config';
import {
  IAuthenticateUserPostRequestPayload,
  IAuthenticateUserPostResponse,
  IProfile,
  ISendOTPPostRequestPayload,
  ISendOTPPostResponse,
} from '../../types';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { userDataMapper } from '../../utils';
import { startAuthentication } from '@simplewebauthn/browser';
import { userDeviceInfo } from '@unified-checkout/kit-components';
import { passkey } from '../inner_layout/passkey';
import { useResendOtp } from '../inner_layout/auth';
import { AxiosError } from 'axios';

interface IAuth {
  authenticateUser: (
    payload: IAuthenticateUserPostRequestPayload
  ) => Promise<IAuthenticateUserPostResponse>;
  sendOTP: (
    payload: ISendOTPPostRequestPayload
  ) => Promise<ISendOTPPostResponse>;
}

type ErrorResponse = {
  message: string;
  detail?: string
};

export type PasskeysFailedEvent =
  | 'OTP_REQUIRED'
  | 'PASSKEY_FAILED'

export const auth: IAuth = {
  authenticateUser: (payload) =>
    http.post(`/api/auth/customer/check-and-initiate-auth`, payload),
  sendOTP: (payload) => http.post(`/api/auth/customer/verify-otp`, payload),
};

const passkeyFailureHandler = async (
  customerId: string,
  resendOtpMutate: (variables: {
    customer_id: string;
    sent_to: 'email' | 'phone' | 'both';
  }) => Promise<any>
) => {
  toast.error('Passkey not recognized. Please login with OTP.');
  await resendOtpMutate({
    customer_id: customerId,
    sent_to: 'phone',
  });
};
interface UseAuthenticateUserProps {
  onPasskeysFailed?: (event: PasskeysFailedEvent, data?: any) => void;
}

export const useAuthenticateUser = ({ onPasskeysFailed }: UseAuthenticateUserProps) => {
  const navigator = useNavigate();
  const handler = useStoreUserData()
  const { mutateAsync: resendOtpMutate, isPending: isPendingResendOtp } =
    useResendOtp();
  const authMutation = useMutation({
    mutationKey: ['auth-authenticate-user'],
    mutationFn: auth.authenticateUser,
    onSuccess: async (data) => {
      const profile = userDataMapper(data, 'LOGIN_VERIFICATION_PASSKEY');
      handler({ data: profile });
      toast.success(data.message);

      if (data.action === 'passkey_auth') {
        let credential;
        try {
          try {
            credential = await startAuthentication({
              optionsJSON: data.publicKey as any,
            });
            const credentialId = credential.id;
            const allowedCredentialIds =
              data.publicKey?.allowCredentials?.map((cred) => cred.id) || [];
            if (!allowedCredentialIds.includes(credentialId)) {
              passkeyFailureHandler(data.customer_id, resendOtpMutate);
              onPasskeysFailed && onPasskeysFailed("PASSKEY_FAILED");
              handler({ currentPhase: 'LOGIN_VERIFICATION_OTP' });
              return;
            }
          } catch (error) {
            passkeyFailureHandler(data.customer_id, resendOtpMutate);
            onPasskeysFailed && onPasskeysFailed("PASSKEY_FAILED");
            handler({ currentPhase: 'LOGIN_VERIFICATION_OTP' });
            return;
          }

          const userData = await passkey.verifyPasskeyLogin({
            customerId: data.customer_id,
            credential: { ...credential, extra: { ...userDeviceInfo } },
          });
          if(userData){
            const profile: IProfile = userDataMapper(userData, 'VERIFIED');
            toast.success(userData.message);
            handler({ data: profile });
            navigator('/');
          }
        } catch (error) {
          passkeyFailureHandler(data.customer_id, resendOtpMutate);
          onPasskeysFailed && onPasskeysFailed('PASSKEY_FAILED');
          handler({
            data: { ...profile, currentPhase: 'LOGIN_VERIFICATION_OTP' },
            currentPhase: 'LOGIN_VERIFICATION_OTP',
          });
          return;
        }
      }
    },
    onError: (error) => {
      const err = error as unknown as {
        response: { data: { message: string }, status: number };
      };
      if (err.response?.status !== 404) {
        toast.error(err?.response.data.message);
      }
      return error;
    },
  });

  return {
    ...authMutation,
    isPendingResendOtp,
  };
};

export const useSendOTP = () => {
  const navigator = useNavigate();
  const handler = useStoreUserData();

  return useMutation({
    mutationKey: ['auth-verify-otp'],
    mutationFn: auth.sendOTP,
    onSuccess: (data) => {
      const profile = userDataMapper(data, 'VERIFIED');
      handler({ data: profile});
      toast.success(data.message);
      navigator('/');
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.response?.data?.detail;

      if (message && !message.includes('No account found') && status !== 423) {
        toast.error(message);
      }
    },
  });
};
